import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { matchRoutes, NavigateOptions, RouteObject, useLocation, useNavigate } from 'react-router-dom'

import logger from '@cash-web/shared/util-logger'
import { useReducer } from '@cash-web/shared-data-access-redux-inspect'
import { getBlockerDataDetail } from '@cash-web/shared-flow-data-access-context'
import { getActiveBlocker } from '@cash-web/shared-flow-feature-main'
import { JourneyContext, JourneyNavigate, JourneyParentContext } from '@cash-web/shared-journey-data-access-context'
import {
  BrowserNavigatePath,
  JourneyData,
  JourneyRoute,
  JourneyScope,
  JourneyTarget,
} from '@cash-web/shared-journey-util-main'
import { createSingleUseFlowToken } from '@cash-web/shared-util-fetch'

import { createExternalStore } from './createExternalStore'
import { createMemoryRouterWithContext } from './createMemoryRouterWithContext'
import { getModalVariant, getTarget, getTargetHistory } from './getTargetHistory'
import { JourneyBaggageMiddleware } from './JourneyBaggageMiddleware'
import { JourneyFlowInstance } from './JourneyFlowInstance'
import { JourneyAction, journeyPaneReducer, JourneyState } from './journeyPaneReducer'
import { JourneyPaneRenderer } from './JourneyPaneRenderer'
import { JourneyStore, JourneyStoreInstance, JourneyStoreProvider } from './JourneyStoreProvider'

// Import CommandKJourneyBridge with lazy loading to avoid dependency issues
let CommandKJourneyBridge: React.ComponentType | null = null
try {
  const devToolsModule = require('@cash-web/shared-devtools-feature-main')
  CommandKJourneyBridge = devToolsModule?.CommandKJourneyBridge || null
} catch {
  // If devtools module is not available, that's fine - CommandK just won't be available
}

let EchoJourneyBridge: React.ComponentType | null = null
try {
  const echoModule = require('@cash-web/shared-echo-data-access-main')
  EchoJourneyBridge = echoModule?.EchoJourneyBridge || null
} catch {
  // Echo module not available
}

export const JourneyPaneProvider = ({ children, routes }: PropsWithChildren<{ routes: RouteObject[] }>) => {
  const browserRouterNavigate = useNavigate()
  const location = useLocation()

  const journeyStore = useRef<JourneyStore>()
  if (!journeyStore.current) {
    journeyStore.current = createExternalStore<JourneyStoreInstance>()
  }
  const [loadingRoute, setLoadingRoute] = useState<string | undefined>(undefined)

  const [state, dispatch] = useReducer<JourneyState, JourneyAction>(
    journeyPaneReducer,
    {
      history: [],
    },
    { name: 'JourneyProvider' }
  )

  const cancel = useCallback(
    (journeyId?: string) => {
      const journeyData = journeyId ? state.history.find(h => h.journeyId === journeyId) : state.history.at(-1)
      journeyData?.opts?.onCancel?.()

      dispatch({ type: 'JOURNEY_CANCEL', journeyId })
    },
    [state.history]
  )

  // Close out of all journeys and cancel any active flows.
  const cancelAll = useCallback(
    (options: { excludeTargets?: JourneyTarget[]; rootId?: string } = {}) => {
      state.history.forEach(h => {
        const flow = journeyStore.current?.getSnapshot(h.journeyId)?.flow
        if (flow?.isInProgress) {
          flow.cancel({ force: true })
        }
        h.opts?.onCancel?.()
      })
      dispatch({ type: 'JOURNEY_CANCEL_ALL', excludeTargets: options.excludeTargets, rootId: options.rootId })
    },
    [state.history]
  )

  const createJourney = useCallback(
    async (path: string, { parent, ...opts }: JourneyData['opts'] = {}) => {
      const [pathname, search = ''] = path.split('?')
      const journeyId = createSingleUseFlowToken()

      if (!routes) {
        logger.error(new Error('Journey routes are not defined'))
        return { journey: undefined, complete: false }
      }

      const matches = matchRoutes(routes, path)
      const routeConfig = matches?.[matches.length - 1]?.route as JourneyRoute

      const scope = (opts?.scope ?? {}) as JourneyScope

      const journeyData: JourneyData = {
        journeyId,
        rootId: parent?.rootId ?? journeyId,
        path,
        location: location.key,
        opts,
        routeConfig,
      }

      // we need to exclude the moneybot journey, because it's a special case
      const lastTarget = parent ? getTarget(parent) : undefined
      // we assume if we aren't passed a target, the target will be maintained from the last journey, otherwise we default to modal
      scope['target'] = getTarget(journeyData) ?? lastTarget ?? 'modal'

      if (scope.target === 'modal') {
        // ensure we have a sensible default if one isn't specified
        opts.modalVariant = getModalVariant(journeyData) ?? 'default'
      }

      if (scope['target'] === 'outlet:moneybot' || lastTarget === 'outlet:moneybot') {
        journeyData.location = 'global'
      }

      // we need to get the last scope and merge it with the new scope
      const lastTargetJourney = getTargetHistory(state.history)[scope.target]?.at(-1)

      journeyData.opts = {
        ...opts,
        scope: {
          ...(lastTargetJourney?.opts?.scope ?? {}),
          ...scope,
        },
      }

      const browserNavigate = (path: BrowserNavigatePath, options?: NavigateOptions) => {
        cancelAll({ excludeTargets: ['outlet:moneybot', 'outlet:moneybot-action'] })
        const appName = path.split('/')[1]
        const currentApp = typeof window !== 'undefined' ? window.location.pathname.split('/')[1] : ''

        if (appName && appName !== currentApp) {
          window.open(path, '_blank')
        } else {
          const inAppPath = path.replace(`/${appName}`, '')
          // merge opts.state from journey.navigate with state from options argument
          browserRouterNavigate(inAppPath, {
            replace: options?.replace != null ? options?.replace : opts?.replace,
            state: { ...opts?.state, ...options?.state },
          })
        }
      }

      const router = createMemoryRouterWithContext(
        routes,
        {
          initialEntries: [
            {
              pathname: pathname,
              search: search ? `?${search}` : '',
              state: { ...opts?.state, scope: journeyData.opts?.scope },
            },
          ],
        },
        { browserNavigate }
      )
      journeyStore.current?.updateProperty(journeyId, 'router', router)

      // Wait for the router's loader to complete
      if (routeConfig?.loader) {
        await new Promise<void>(resolve => {
          setLoadingRoute(router.state.navigation.state === 'loading' ? router.state.location?.pathname : undefined)
          if (router.state.navigation.state === 'idle') {
            resolve()
          } else {
            const unsubscribe = router.subscribe(state => {
              setLoadingRoute(state.navigation.state === 'loading' ? state.location?.pathname : undefined)
              if (state.navigation.state === 'idle') {
                unsubscribe()
                resolve()
              }
            })
          }
        })
      }

      // `currentRoute` may differ from `routeConfig` (defined above) due to redirects, so we follow redirects to get the correct route config
      const currentRoute = router.state.matches?.[router.state.matches.length - 1]?.route as JourneyRoute
      const loaderData = router.state.loaderData?.[currentRoute?.id]

      const blockerDescriptors = loaderData?.response_context?.scenario_plan?.blocker_descriptors
      const hasStatusResult = loaderData?.response_context?.status_result != null

      const blockerData = getBlockerDataDetail(
        getActiveBlocker([], loaderData?.response_context?.scenario_plan?.blocker_descriptors ?? []) ?? {}
      )
      journeyData.initialBlocker = blockerData.type

      const isFlowWithNoBlockers = blockerDescriptors?.length === 0 && !hasStatusResult
      const hasComponent =
        currentRoute?.lazy != null || currentRoute?.Component != null || currentRoute?.element != null

      return {
        journey: journeyData,
        complete: isFlowWithNoBlockers || !hasComponent,
      }
    },
    [state.history, routes, location.key, cancelAll, browserRouterNavigate]
  )

  const navigate = useCallback<JourneyNavigate>(
    async (path, opts) => {
      const isJourneyInProgress = state.history.some(h => h.path === path)

      if (isJourneyInProgress) {
        // check to see if the journey is already in progress in the history
        for (let i = state.history.length - 1; i >= 0; i--) {
          const journeyHistory = state.history[i]
          dispatch({ type: 'JOURNEY_COMPLETE' })
          if (journeyHistory.path === path) {
            break
          }
        }
      }
      const { journey, complete } = await createJourney(path, opts)

      // If journey creation failed, return early
      if (!journey) {
        return ''
      }

      // journeys completed without blockers and routes that render no components/elements should not be added to history
      if (!complete) {
        dispatch({ type: 'JOURNEY_START', journey })
      }

      return journey.journeyId
    },
    [createJourney, state.history]
  )

  const complete = useCallback(
    (journeyId?: string) => {
      const journeyData = journeyId ? state.history.find(h => h.journeyId === journeyId) : state.history.at(-1)
      journeyData?.opts?.onComplete?.()
      dispatch({ type: 'JOURNEY_COMPLETE', journeyId })
    },
    [state.history]
  )

  const hasParentTarget = useCallback(
    (target: JourneyTarget) => {
      return state.history.some(h => (h.opts?.scope?.['target'] ?? 'modal') === target)
    },
    [state.history]
  )

  const matches = useCallback(
    (path?: string) => {
      if (!path || !routes) return false
      const knownFallbackBasenames = ['/', '/dl/view']
      const routeMatches = matchRoutes(routes, path) ?? []
      return routeMatches.filter(match => !knownFallbackBasenames.includes(match.pathnameBase)).length > 0
    },
    [routes]
  )

  const preload = useCallback(
    (path: string) => {
      if (!routes) return
      const matches = matchRoutes(routes, path) ?? []
      matches.forEach(match => match.route.lazy?.())
    },
    [routes]
  )

  const handleCompleteFlow = useCallback(() => undefined, [])

  const isFlowLoading = useCallback(
    (tag: string) => {
      return state.history.some(journey => {
        if (journey.opts?.scope?.tag !== tag) return false

        // Check if this journey has a flow that is loading
        const flow = journeyStore.current?.getSnapshot(journey.journeyId)?.flow
        return flow?.isLoading === true
      })
    },
    [state.history]
  )

  const context = useMemo(
    () => ({
      navigate,
      cancel,
      cancelAll,
      complete,
      isInProgress: (journeyId: string) => state.history.some(entry => entry.journeyId === journeyId),
      isFlowLoading,
      active: state.history.at(-1),
      hasParentTarget,
      matches,
      preload,
      loadingRoute,
    }),
    [
      navigate,
      cancel,
      cancelAll,
      complete,
      state.history,
      isFlowLoading,
      hasParentTarget,
      matches,
      preload,
      loadingRoute,
    ]
  )

  // we should cancel all journeys when the user navigates to a different browser path
  useEffect(() => {
    dispatch({ type: 'JOURNEY_LOCATION_CHANGE', location: location.key })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  return (
    <JourneyContext.Provider value={context}>
      {/* Render CommandK if devtools are available */}
      {CommandKJourneyBridge && <CommandKJourneyBridge />}
      {/* Render EchoJourneyBridge if echo is available */}
      {EchoJourneyBridge && <EchoJourneyBridge />}
      {journeyStore.current != null &&
        state.history.map(journeyData => (
          <JourneyParentContext.Provider key={journeyData.journeyId} value={journeyData}>
            <JourneyBaggageMiddleware
              journey={journeyData}
              flow={journeyStore.current?.getSnapshot(journeyData.journeyId)?.flow}
            >
              <JourneyFlowInstance
                journeyData={journeyData}
                store={journeyStore.current}
                onCompleteFlow={handleCompleteFlow}
                onCancelFlow={() => cancel(journeyData.journeyId)}
              />
            </JourneyBaggageMiddleware>
          </JourneyParentContext.Provider>
        ))}
      <JourneyStoreProvider store={journeyStore.current}>
        <JourneyPaneRenderer onBack={cancel} onClose={cancelAll} history={state.history}>
          {children}
        </JourneyPaneRenderer>
      </JourneyStoreProvider>
    </JourneyContext.Provider>
  )
}
